import { createBrowserNavigationUrl } from "client/Navigation";
import {
	navigateToComposeAppointmentInOwa,
	navigateToOwaAppointment,
	navigateToOwaCalendar
} from "../Email/OwaNavigation/owaNavigator";
import { calendarRouting } from "./CalendarPage";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { CalendarClientPreference, userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { DestinationWindow } from "client/Api/Apps/common";
import { never } from "client_legacy/ndaw/errors";
import { AppointmentIdentifier } from "client/Api/Calendar/calendar";
import { DateOnly } from "client/DateAndTime/dateOnly";

export interface ICalendarClientFeatures {
	doesSupportCreation: boolean;
	doesSupportOverview: boolean;
	doesSupportDetails: boolean;
}

export interface ICalendarClientCreateFeature {
	doesSupportCreation: true;
	createAppointment(): void;
}

export type OpenOverviewOptions = { isInNewWindow?: boolean; date?: DateOnly; };

export interface ICalendarClientOverviewFeature {
	doesSupportOverview: true;
	isExternal: boolean;
	openOverview(options?: OpenOverviewOptions): void;
}

export interface ICalendarClientDetailsFeature {
	doesSupportDetails: true;
	openDetails(options: { isInNewWindow?: boolean; appointmentIdentifier: AppointmentIdentifier; }): void;
}

export class DisabledCalendarClient implements ICalendarClientFeatures {
	public get doesSupportCreation(): false { return false; }
	public get doesSupportOverview(): false { return false; }
	public get doesSupportDetails(): false { return false; }
}

export class OwaCalendarClient implements
	ICalendarClientCreateFeature,
	ICalendarClientOverviewFeature,
	ICalendarClientDetailsFeature,
	ICalendarClientFeatures
{
	private readonly _shouldOpenInNewWindowByDefault: boolean;
	public get doesSupportOverview(): true { return true; }
	public get doesSupportCreation(): true { return true; }
	public get doesSupportDetails(): true { return true; }
	public get isExternal(): true { return true; }

	constructor(
		shouldOpenInNewWindowByDefault: boolean
	) {
		this._shouldOpenInNewWindowByDefault = shouldOpenInNewWindowByDefault;
	}

	public openOverview(options: OpenOverviewOptions = {}): void {
		const {
			isInNewWindow = this._shouldOpenInNewWindowByDefault
		} = options;

		// The date is ignored: we have no OWA deeplink that opens the calendar view on a specific date.
		navigateToOwaCalendar(isInNewWindow);
	}

	public createAppointment(): void {
		navigateToComposeAppointmentInOwa();
	}

	public openDetails(options: { isInNewWindow?: boolean; appointmentIdentifier: AppointmentIdentifier; }): void {
		const {
			appointmentIdentifier,
			isInNewWindow = this._shouldOpenInNewWindowByDefault
		} = options;

		navigateToOwaAppointment(appointmentIdentifier.id, isInNewWindow);
	}
}

export class WorkspaceCalendarClient implements
	ICalendarClientCreateFeature,
	ICalendarClientOverviewFeature,
	ICalendarClientFeatures
{
	private readonly _shouldOpenInNewWindowByDefault: boolean;
	public get doesSupportCreation(): true { return true; }
	public get doesSupportOverview(): true { return true; }
	public get doesSupportDetails(): false { return false; }
	public get isExternal(): false { return false; }

	constructor(
		shouldOpenInNewWindowByDefault: boolean
	) {
		this._shouldOpenInNewWindowByDefault = shouldOpenInNewWindowByDefault;
	}

	public openOverview(options: OpenOverviewOptions = {}): void {
		const {
			isInNewWindow = this._shouldOpenInNewWindowByDefault,
			date
		} = options;

		const url: string = createBrowserNavigationUrl(
			calendarRouting.page({
				queryParams: { date: date?.toISOString() }
			})
		);

		navigateTo(url, isInNewWindow);
	}

	public createAppointment(): void {
		navigateToComposeAppointmentInOwa();
	}
}

export type CalendarClient =
	| DisabledCalendarClient
	| WorkspaceCalendarClient
	| OwaCalendarClient;

export function getEffectiveCalendarClient(): CalendarClient {
	const { calendar, exchange } = userConfiguration();
	if (!exchange.isEnabled || !exchange.isConfigured) {
		return new DisabledCalendarClient();
	}
	switch (calendar.clientPreference) {
		case CalendarClientPreference.UseNone:
			return new DisabledCalendarClient();
		case CalendarClientPreference.Workspace:
			return new WorkspaceCalendarClient(calendar.destinationWindow === DestinationWindow.NewWindow);
		case CalendarClientPreference.OutlookWebApp:
			return new OwaCalendarClient(calendar.destinationWindow === DestinationWindow.NewWindow);
		default:
			never(calendar.clientPreference);
	}
}